import { useGetOrg } from "@src/api/organizations/get-organization";
import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { useActiveAgenticLogicView } from "@src/features/ai/pages/agentic-logic/surface/useActiveAgenticLogicView";
import { useSelector } from "@src/store";
import { getIsNewSession } from "@src/store/slices/aiChat";
import { selectAgenticLogicRuleIdeas } from "@src/store/slices/appSettings";
import { SuggestionChipRow } from "./SuggestionChipRow";
import { useRailComposerCardOpen } from "./railComposerCard";

/**
 * The rule ideas Atlas generated for this store, offered as a scrollable row of
 * chips directly above the rail's composer. Picking one sends it as the first
 * message.
 *
 * Offered on ONE surface and view: the embedded rule builder's creation view —
 * the "add a rule" flow taken over by an upsell's settings dialog, where the
 * author arrived with a placement in mind and no rule yet, and the ideas are
 * written for that placement's serving surface. The standalone
 * surfaces have their own creation view, which lists these same ideas as its
 * page content, and editing an existing rule is never a cold start: an idea
 * there would offer to build a different rule inside the rule on screen.
 *
 * A cold-start affordance only: once the conversation has turns, an idea would
 * offer to start a different rule inside a session that is already about one,
 * so the row disappears. It also steps aside for a parked card ask, which owns
 * the space above the composer while it is on screen.
 *
 * The ideas are read from the store the creation view already fills — this
 * never generates them, because generating is a workflow run and two owners
 * would run it twice.
 */
export const AtlasSuggestionChips = () => {
  const orgId = useGetOrg().data?.org?.id;
  const isNewSession = useSelector(getIsNewSession);
  const { inputRequirement, submitInput, disabled, streaming } =
    useExecutionPanelContext();
  // This row renders inside the rail, which lives outside the page tree, so
  // the surface comes from the registry. An embedded surface namespaces its
  // view param away from the host's own query string.
  const { surface, view } = useActiveAgenticLogicView();
  // The same set the creation view holds: ideas are cached per placement
  // surface, so reading them without the surface would show the row a
  // different set from the one the page beside it generated.
  const ideas = useSelector(
    selectAgenticLogicRuleIdeas(orgId, surface?.placementSurface)
  );
  const onEmbeddedCreation =
    surface?.kind === "embedded" && view.kind === "new";

  // Either card on the composer — a parked question or the rail's own cart
  // ask — sits directly on the input; the row comes back once it is dismissed
  // (ENG-7894).
  const cardOpen = useRailComposerCardOpen();
  const cardAskParked = inputRequirement.userInput.type !== "text" || cardOpen;
  if (
    !onEmbeddedCreation ||
    ideas.length === 0 ||
    !isNewSession ||
    cardAskParked ||
    disabled ||
    streaming
  ) {
    return null;
  }

  return (
    <SuggestionChipRow
      label="Rule ideas for your store"
      chips={ideas.map((idea) => ({
        key: idea,
        label: idea,
        onClick: () => submitInput({ type: "text", content: idea }),
      }))}
    />
  );
};
